import { Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { LoadingOptions } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class LoadingService {
  constructor(public loadingCtrl: LoadingController, private translate: TranslateService) { }

  public loading: { instance: HTMLIonLoadingElement; status: boolean } = { instance: null, status: false };
  isLoading = false;
  private isShowing = false; // Mutex para evitar condiciones de carrera
  private showPromise: Promise<void> | null = null; // Promise para evitar múltiples llamadas simultáneas
  
  public async show(message?,) {
    // Si ya hay una operación de show en progreso, esperar a que termine
    if (this.showPromise) {
      await this.showPromise;
      return;
    }
    
    // Si ya hay un loading mostrado, no crear otro
    if (this.loading.status === true) {
      return;
    }
    
    // Crear promise para evitar múltiples llamadas simultáneas
    this.showPromise = this._doShow(message);
    
    try {
      await this.showPromise;
    } finally {
      this.showPromise = null;
    }
  }

  private async _doShow(message?) {
    // Activar mutex
    this.isShowing = true;
    
    try {
      if (!this.loading.instance) {
        this.loading.instance = await this.loadingCtrl.create(this.options(message));
      }

      if (this.loading.status === false) {
        await this.loading.instance.present();
      }

      this.loading.status = true;
    } finally {
      // Liberar mutex
      this.isShowing = false;
    }
  }

  public async hide() {
    // Limpiar showPromise si existe
    if (this.showPromise) {
      this.showPromise = null;
    }
    
    // Liberar mutex si está activo
    if (this.isShowing) {
      this.isShowing = false;
    }
    
    if (this.loading.instance !== null && this.loading.status === true) {
      try {
        await this.loading.instance.dismiss();
        this.loading = { instance: null, status: false };
      } catch (error) {
        // Forzar limpieza en caso de error
        this.loading = { instance: null, status: false };
      }
      return;
    }
    
    try {
      await this.loadingCtrl.dismiss();
    } catch (error) {
      // Error silencioso en fallback
    }
  }

  public options(message?): LoadingOptions {
    return {
      spinner: 'crescent',
      message: message ? message : this.translate.instant('LOADING'),
      translucent: true
    };
  }

  // Método para forzar la limpieza completa del loading
  public async forceCleanup() {
    this.isShowing = false;
    this.showPromise = null;
    this.loading = { instance: null, status: false };
    try {
      await this.loadingCtrl.dismiss();
    } catch (error) {
      // Error silencioso en forceCleanup
    }
  }
}
